import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChannelApi } from '../../Services/ChannelApiProvider';
import { useThreadApi } from '../../Services/ThreadApiProvider';
import Button from '../Buttons';
import { getDateRangeByPeriod } from '../DateRangeSelector';
import NestedDropdown from '../NestedDropdown';
import ChatLoader from './Loader';
import ThreadItem from './ThreadItem';

const ChatsSidebar = () => {
  const { organizationId, chatId } = useParams();
  const navigate = useNavigate();

  const {
    isOnline,
    loading,
    threads,
    totalThreads,
    haveMoreThreads,
    filters,
    updateFiltersAndFetch,
    loadMore,
  } = useThreadApi();

  const { getChannels } = useChannelApi();

  const [channelFilterOptions, setChannelFilterOptions] = useState([]);
  const [searchName, setSearchName] = useState(filters.searchName || '');
  const [searchMessageText, setSearchMessageText] = useState(filters.searchMessageText || '');

  useEffect(() => {
    setSearchName(filters.searchName || '');
  }, [filters.searchName]);

  useEffect(() => {
    setSearchMessageText(filters.searchMessageText || '');
  }, [filters.searchMessageText]);

  const handleSearchChange = (e) => {
    setSearchName(e.target.value);
  };

  const fetchChannels = useCallback(async () => {
    try {
      const response = await getChannels(0, 500);
      const items = response?.items || [];
      const channelOptions = items.map((channel) => ({
        label: channel.name,
        value: channel.id,
      }));
      setChannelFilterOptions([{ label: 'All', value: 'all' }, ...channelOptions]);
    } catch (err) {
      console.error('Failed to load channels for filter', err);
    }
  }, [getChannels]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      updateFiltersAndFetch(
        {
          searchName: searchName,
          searchMessageText: '',
        },
        true,
      );
    }
  };

  useEffect(() => {
    if (!chatId && threads && threads.length > 0) {
      navigate(`/organizations/${organizationId}/chats/${threads[0].id}`);
      return;
    }

    if (
      chatId &&
      threads &&
      threads.length > 0 &&
      !threads.some((thread) => thread.id === chatId)
    ) {
      return;
    }
  }, [chatId, threads]);

  const handleDropdownCheckboxChange = (selectedValues) => {
    const newFilters = {};

    if (selectedValues.message_search !== undefined) {
      const messageSearch = selectedValues.message_search;

      newFilters.searchMessageText = messageSearch;
      setSearchMessageText(messageSearch);

      if (messageSearch.length > 0) {
        newFilters.searchName = '';
        setSearchName('');
      }
    }

    const threadFilter = selectedValues.thread_filter ?? [];

    // Handle isActive filter (active/inactive pair)
    const hasActive = threadFilter.includes('active');
    const hasInactive = threadFilter.includes('inactive');
    if (hasActive && hasInactive) {
      newFilters.isActive = undefined; // Both checked - omit filter
    } else if (hasActive) {
      newFilters.isActive = true;
    } else if (hasInactive) {
      newFilters.isActive = false;
    } else {
      newFilters.isActive = undefined; // None checked - omit filter
    }

    // Handle isVirtual filter (is_virtual/not_virtual pair)
    const hasVirtual = threadFilter.includes('is_virtual');
    const hasNotVirtual = threadFilter.includes('not_virtual');
    if (hasVirtual && hasNotVirtual) {
      newFilters.isVirtual = undefined; // Both checked - omit filter
    } else if (hasVirtual) {
      newFilters.isVirtual = true;
    } else if (hasNotVirtual) {
      newFilters.isVirtual = false;
    } else {
      newFilters.isVirtual = undefined; // None checked - omit filter
    }

    // Handle isModerated filter (is_moderated/not_moderated pair)
    const hasModerated = threadFilter.includes('is_moderated');
    const hasNotModerated = threadFilter.includes('not_moderated');
    if (hasModerated && hasNotModerated) {
      newFilters.isModerated = undefined; // Both checked - omit filter
    } else if (hasModerated) {
      newFilters.isModerated = true;
    } else if (hasNotModerated) {
      newFilters.isModerated = false;
    } else {
      newFilters.isModerated = undefined; // None checked - omit filter
    }

    const timeRange = selectedValues.time_range ?? [];
    if (timeRange.length > 0) {
      const range = getDateRangeByPeriod(timeRange[0]);
      if (range) {
        newFilters.startDate = range.start_date;
        newFilters.endDate = range.end_date;
      }
    }
    const channelKeys = selectedValues.channel_filter ?? [];

    if (channelKeys[channelKeys.length - 1] === 'all') {
      selectedValues.channel_filter = ['all'];
      newFilters.channelKeys = [];
    } else {
      selectedValues.channel_filter = channelKeys.filter((k) => k !== 'all');
      newFilters.channelKeys = selectedValues.channel_filter;
    }

    updateFiltersAndFetch(newFilters, true);
  };

  const threadFilterTypes = [
    { label: 'Active', value: 'active' },
    { label: 'Closed', value: 'inactive' },
    { label: 'AI Active', value: 'is_virtual' },
    { label: 'AI Inactive', value: 'not_virtual' },
    { label: 'Moderated', value: 'is_moderated' },
    { label: 'Not Moderated', value: 'not_moderated' },
  ];

  const periods = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
    { label: 'All time', value: 'max' },
  ];

  const dropdownSection = [
    {
      value: 'thread_filter',
      label: 'Thread Filter',
      type: 'multiple',
      children: threadFilterTypes,
    },
    {
      value: 'channel_filter',
      label: 'Channel Filter',
      type: 'multiple',
      children: channelFilterOptions,
    },
    {
      value: 'time_range',
      label: 'Time Range',
      type: 'single',
      children: periods,
    },
    {
      value: 'message_search',
      label: 'Message Search',
      type: 'search',
    },
  ];

  return (
    <div className="chats-sidebar d-flex flex-column">
      <div className="sidebar-header">
        <div className="toolbar">
          <div className="title-unread">
            <div className="title">Чати</div>
            <div className="unread">{totalThreads}</div>
            {isOnline && (
              <div>
                <i className="fa-solid fa-globe"></i>
              </div>
            )}
          </div>
        </div>
        <div className="search d-flex" style={{ gap: '15px', paddingBottom: '15px' }}>
          <div className="search-wrapper">
            <div className="icon-wrapper">
              <i className="fa-solid fa-magnifying-glass"></i>
            </div>
            <input
              className="search-input"
              type="text"
              onChange={handleSearchChange}
              value={searchName}
              onKeyDown={handleKeyDown}
              placeholder="Search by name..."
            />
          </div>
          <NestedDropdown
            sections={dropdownSection}
            onChange={handleDropdownCheckboxChange}
            externalSearchValue={{ message_search: searchMessageText }}
            defaultValues={{
              thread_filter: ['active'],
              time_range: ['max'],
              channel_filter: ['all'],
            }}
            placeholder={'Search by message...'}
          />
        </div>
      </div>
      <div className="chats-list">
        {loading && <ChatLoader style={{ width: '100%', height: '100%' }} />}
        {!loading && (
          <>
            {threads.length === 0 && (
              <div className="no-chats">
                <span className="no-chats-title">
                  It looks like there’s nothing, but we should check again.
                </span>
                <span className="no-chats-image"></span>
              </div>
            )}

            {threads.map((element) => (
              <ThreadItem key={element.id} thread={element} />
            ))}

            {haveMoreThreads && (
              <div className="tabs d-flex justify-center">
                <Button
                  variant="primary"
                  size="small"
                  style={{
                    margin: '0 auto',
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'center',
                  }}
                  onClick={loadMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatsSidebar;
